import { EnumDialogName } from '@calm/evexia-analytics';
import { Theme } from '@rhg/libs/material';
import SimpleDialog from '@rhg/libs/material/simple-dialog';
import { ComponentType, useId } from 'react';
import { LoadingDialogStepContent } from '../generation-steps';
import { LoadingDialogQuoteContent } from '../random-quote';
import {
    LoadingDialogContentProps,
    LoadingDialogOptions,
    LoadingDialogVariant,
} from '../types';

export type { LoadingDialogVariant };

interface LoadingDialogProps {
    isOpen: boolean;
    options: Omit<LoadingDialogOptions, 'timeout'>;
    /** Defaults to `quote`, the variant that can swap its title for a random quote */
    variant?: LoadingDialogVariant;
}

const styleLoadingDialog = {
    zIndex: (theme: Theme) => theme.zIndex.loadingDialog,
};

const styleDialogPaper = {
    backgroundColor: (theme: Theme) => theme.palette.background.paper,
};

const styleContentBox = { paddingBottom: 16.5 };

const contentByVariant: Record<
    LoadingDialogVariant,
    ComponentType<LoadingDialogContentProps>
> = {
    quote: LoadingDialogQuoteContent,
    steps: LoadingDialogStepContent,
};

export const LoadingDialog = ({
    isOpen,
    options,
    variant = 'quote',
}: LoadingDialogProps) => {
    const id = useId();
    const Content = contentByVariant[variant];

    return (
        <SimpleDialog
            PaperProps={{ sx: styleDialogPaper }}
            trackDialog={options?.trackDialog || EnumDialogName.Toast}
            isOpen={isOpen}
            onClose={() => options?.onClose && options.onClose()}
            aria-labelledby={id}
            hideCloseButton
            background={options?.background}
            fullScreen
            disableBackdropClick
            disableEscapeKeyDown
            sx={styleLoadingDialog}
            transitionDuration={options?.transitionDuration}
            content={
                <Content
                    isReady={isOpen}
                    title={options?.title}
                    titleVariant={options?.titleVariant}
                    titleId={id}
                    subtitle={options?.subtitle}
                    subtitleVariant={options?.subtitleVariant}
                    boxStyles={styleContentBox}
                    isConciergeLoadingDialog={options?.isConciergeLoadingDialog}
                    shouldShowQuote={options?.shouldShowQuote}
                    isGenerationPending={options?.isGenerationPending}
                    onGenerationComplete={options?.onGenerationComplete}
                />
            }
        />
    );
};
